import { z } from "zod";

import { COGNITO_ADMIN_GROUP } from "@recobra/core";

export type VisibilityRule = "*" | readonly string[];

const DEFAULT_VISIBILITY = [COGNITO_ADMIN_GROUP] as const;

declare module "zod" {
  interface GlobalMeta {
    visibleTo?: VisibilityRule;
  }
}

const VisibilityMetaSchema = z.object({
  visibleTo: z.union([z.literal("*"), z.array(z.string())]).optional(),
});

const publicSchema = (schema: z.core.$ZodType): z.ZodType => {
  // Zod exposes object/array child schemas as core schemas, but the public
  // parser API is still present at runtime.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return schema as z.ZodType;
};

const isVisibilityRuleVisible = (rule: VisibilityRule, callerGroups: readonly string[]): boolean =>
  rule === "*" || rule.some((group) => callerGroups.includes(group));

const nestedDefaultVisibility = (containingFieldVisibility: VisibilityRule): VisibilityRule =>
  // Public containers must not make newly added, unannotated descendants public.
  containingFieldVisibility === "*" ? DEFAULT_VISIBILITY : containingFieldVisibility;

const fieldVisibility = (schema: z.ZodType): VisibilityRule | undefined => {
  const parsed = VisibilityMetaSchema.safeParse(schema.meta());
  const visibility = parsed.success ? parsed.data.visibleTo : undefined;
  if (visibility) return visibility;

  if (
    schema instanceof z.ZodOptional ||
    schema instanceof z.ZodNullable ||
    schema instanceof z.ZodDefault
  ) {
    return fieldVisibility(publicSchema(schema.unwrap()));
  }

  return undefined;
};

const isVisibleUnionItem = (
  schema: z.ZodUnion,
  item: unknown,
  callerGroups: readonly string[],
): boolean => {
  const option = schema.options.find(
    (candidate) => publicSchema(candidate).safeParse(item).success,
  );
  const rule = option ? fieldVisibility(publicSchema(option)) : undefined;

  return !rule || isVisibilityRuleVisible(rule, callerGroups);
};

export const visibleSchema = (
  schema: z.ZodType,
  callerGroups: readonly string[],
  defaultVisibility: VisibilityRule = DEFAULT_VISIBILITY,
): z.ZodType => {
  if (schema instanceof z.ZodOptional) {
    return z.optional(
      visibleSchema(publicSchema(schema.unwrap()), callerGroups, defaultVisibility),
    );
  }

  if (schema instanceof z.ZodNullable) {
    return z.nullable(
      visibleSchema(publicSchema(schema.unwrap()), callerGroups, defaultVisibility),
    );
  }

  if (schema instanceof z.ZodDefault) {
    const inner = visibleSchema(publicSchema(schema.unwrap()), callerGroups, defaultVisibility);
    return inner.default(() => inner.parse((schema as any).def.defaultValue));
  }

  if (schema instanceof z.ZodArray) {
    const itemSchema = publicSchema(schema.unwrap());
    const element = visibleSchema(itemSchema, callerGroups, defaultVisibility);
    const array = element === itemSchema ? schema : z.array(element);

    return itemSchema instanceof z.ZodUnion
      ? z.preprocess(
          (value) =>
            Array.isArray(value)
              ? value.filter((item) => isVisibleUnionItem(itemSchema, item, callerGroups))
              : value,
          array,
        )
      : array;
  }

  if (schema instanceof z.ZodUnion) {
    const options = schema.options.map((option) =>
      visibleSchema(publicSchema(option), callerGroups, defaultVisibility),
    );
    const [first, second, ...rest] = options;
    if (!first || !second) return schema;

    return z.union([first, second, ...rest]);
  }

  if (schema instanceof z.ZodRecord) {
    const valueSchema = publicSchema(schema.valueType);
    const value = visibleSchema(valueSchema, callerGroups, defaultVisibility);

    return value === valueSchema ? schema : schema.clone({ ...schema.def, valueType: value });
  }

  if (!(schema instanceof z.ZodObject)) return schema;

  const shape: Record<string, z.core.$ZodType> = schema.shape;
  const entries = Object.entries(shape);
  const nextShape = Object.fromEntries(
    entries
      .filter(([, value]) => {
        const rule = fieldVisibility(publicSchema(value)) ?? defaultVisibility;

        return isVisibilityRuleVisible(rule, callerGroups);
      })
      .map(([key, value]) => {
        const rule = fieldVisibility(publicSchema(value)) ?? defaultVisibility;

        return [
          key,
          visibleSchema(publicSchema(value), callerGroups, nestedDefaultVisibility(rule)),
        ];
      }),
  );

  return z.object(nextShape);
};
